<script setup>
import Highcharts from "highcharts";
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import * as calc from '../calc.js';
import { useShared } from '../state.js';
import { usePersisted } from '../use_persisted.js';
import { scopedKey, useSetups } from '../setups.js';
import { useTheme } from '../use_theme.js';
import { useToasts } from '../use_toasts.js';
import { useDeferredCompute } from '../use_deferred_compute.js';
import RecomputeButton from './recompute_button.vue';
import UnitSelect from './unit_select.vue';

const { inputs, computedResult, isStale, recompute, getLevelMarksTable } = useShared();
const { currentTheme } = useTheme();
const { pushToast } = useToasts();
const { activeSetupId } = useSetups();

let lastGraphError = null;

// Scoped to the active setup — see mark.vue for why.
const input = usePersisted(scopedKey('graph-inputs', activeSetupId.value), {
  unit: 'm',
  max_distance: 150,
});
const chartContainer = ref(null);
let chart = null;
let resizeObserver = null;

// ─── Reactive data so watchers can observe changes ───
const graphData = ref([]);
const measuredData = ref([]);
const maxMark = ref(100 / inputs.sight_settings.sight_scale);
const minMark = ref(0);

// ─── Computing part: pure data, no chart dependency ───
async function compute() {
  console.log("Computing graph data");

  graphData.value = [];
  measuredData.value = [];
  maxMark.value = 100 / inputs.sight_settings.sight_scale;
  minMark.value = 0;

  if (isStale.value) {
    await recompute();
  }

  const r = computedResult.value;
  const cut_angle = 0;
  const measured = inputs.marks;

  // Clamp so a stray huge/invalid value in the persisted max-distance
  // input can't hang the browser on thousands of solver iterations (see
  // full_marks.vue's same clamp).
  const maxDistance = Math.min(Math.max(Number(input.value.max_distance) || 0, 3), 1000);

  try {
    for (let i = 0; i < measured.length; i++) {
      let d = measured[i].inM() / calc.toScale(input.value.unit);
      let m = measured[i].mark;
      let s = measured[i].sight_bar_position;

      if (s != inputs.sight_settings.sight_bar_position) {
        let a = calc.markToAngle(inputs.sight_settings, s, m - r.offset,
          measured[i].inM(), cut_angle);
        let mAdjusted = calc.angleToMark(inputs.sight_settings,
          inputs.sight_settings.sight_bar_position,
          a, d, cut_angle) + r.offset;

        console.log(`Converted mark ${d} = ${m} at ${s} to ${mAdjusted} at ${inputs.sight_settings.sight_bar_position}`);
        measuredData.value.push([d, mAdjusted]);
      } else {
        measuredData.value.push([d, m]);
      }
    }

    for (let i = 0; i < measuredData.value.length; i++) {
      let m = measuredData.value[i][1];
      if (m > maxMark.value) maxMark.value = m;
      else if (m < minMark.value) minMark.value = m;
    }

    // Reads the shared level-ground table (see state.js) instead of
    // sweeping calculateMark itself — that table is already built at a
    // finer resolution (0.25m) than this panel plots at, so every row here
    // is an exact value from it, not an approximation.
    const scale = calc.toScale(input.value.unit);
    const maxDistanceM = maxDistance * scale;
    const lastMeasuredM = measured.length ? measured[measured.length - 1].inM() : 0;
    const table = await getLevelMarksTable();

    for (const row of table) {
      if (row.distance > maxDistanceM) break;
      if (row.mark < minMark.value) minMark.value = row.mark;
      if (row.distance > lastMeasuredM + 10 && row.mark > maxMark.value) break;

      graphData.value.push([row.distance / scale, row.mark]);
    }

    lastGraphError = null;

  } catch (error) {
    console.log('error computing marks:', error.message);
    console.log(error.stack);
    if (error.message !== lastGraphError) {
      lastGraphError = error.message;
      pushToast(`Graph: ${error.message}`);
    }
  }
}

// ─── Graphing part: theme colors from CSS variables ───
function getChartColors() {
  const styles = getComputedStyle(document.documentElement);
  return {
    bg:       styles.getPropertyValue('--chart-bg').trim(),
    text:     styles.getPropertyValue('--chart-text').trim(),
    grid:     styles.getPropertyValue('--chart-grid').trim(),
    axisLine: styles.getPropertyValue('--chart-axis-line').trim(),
    series:   styles.getPropertyValue('--chart-series').trim(),
    series2:  styles.getPropertyValue('--chart-series-2').trim(),
  };
}

// ─── Graphing part: build full chart config with data + theme ───
function buildChartOptions() {
  const c = getChartColors();

  return {
    chart: {
      backgroundColor: c.bg,
      style: { color: c.text },
      type: 'line',
    },
    title: { text: "Sight marks" },
    colors: [c.series, c.series2],
    xAxis: {
      type: "linear",
      gridLineColor: c.grid,
      lineColor: c.axisLine,
      labels: { style: { color: c.text } },
      title: { text: "Distance (" + input.value.unit + ")", style: { color: c.text } },
    },
    yAxis: {
      title: { text: "Mark", style: { color: c.text } },
      max: maxMark.value,
      min: minMark.value,
      reversed: true,
      gridLineColor: c.grid,
      lineColor: c.axisLine,
      labels: { style: { color: c.text } },
    },
    legend: {
      itemStyle: { color: c.text },
      itemHoverStyle: { color: c.text },
    },
    tooltip: {
      shared: true,
      valueDecimals: 2,
      backgroundColor: c.bg,
      style: { color: c.text },
    },
    plotOptions: {
      series: {
        label: { connectorAllowed: false },
        pointStart: 0,
        pointInterval: 1,
      },
    },
    credits: { enabled: false },
    series: [
      {
        name: "Marks",
        type: "line",
        marker: { enabled: false },
        data: graphData.value,
      },
      {
        name: "Measured",
        type: "scatter",
        marker: { enabled: true },
        data: measuredData.value,
      },
    ],
  };
}

// ─── Graphing part: create or update the chart ───
function drawGraph() {
  console.log("Drawing graph");

  if (!chartContainer.value) return;

  if (chart) {
    chart.update(buildChartOptions(), false);
    chart.reflow();
    chart.redraw();
  } else {
    chart = Highcharts.chart(chartContainer.value, buildChartOptions());
  }
}

// ─── Watcher: inputs changed → recompute → redraw ───
watch([computedResult, input, () => inputs.sight_settings], async () => {
  await nextTick();
  await compute();
  drawGraph();
}, { deep: true, immediate: false });

// ─── Watcher: theme changed → redraw with new colors (data stays same) ───
watch(currentTheme, () => {
  if (!chart) return;
  nextTick(() => {
    drawGraph();
  });
});

// ─── Debounced resize ───
let resizeTimer = null;
function debouncedReflow() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    if (chart) chart.reflow();
  }, 150);
}

// The initial compute+draw is deferred (see use_deferred_compute.js) so the
// panel's own open animation gets to paint and start before this blocks the
// main thread — a later recompute (the watcher above) isn't deferred since
// the panel is already open by then, with no animation racing it.
const pending = useDeferredCompute(async () => {
  await compute();
  drawGraph();
});

onMounted(() => {
  // ResizeObserver catches panel toggle, screen rotation, window resize
  if (chartContainer.value) {
    resizeObserver = new ResizeObserver(debouncedReflow);
    resizeObserver.observe(chartContainer.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (chart) {
    chart.destroy();
    chart = null;
  }
  clearTimeout(resizeTimer);
});
</script>

<template>
  <div class="centered">
    <RecomputeButton />

    <div class="controls">
      <label>Unit:
        <UnitSelect v-model="input.unit" />
      </label>

      <label>Max distance:
        <input type="number" class="input-measure" v-model.number="input.max_distance" />
      </label>
    </div>
  </div>

  <div class="chart-container-wrap">
    <p v-if="pending" class="computing-placeholder">Computing…</p>
    <div ref="chartContainer" class="chart-container"></div>
  </div>
</template>

<style scoped>

.controls {
  display: flex;
  gap: var(--space-lg);
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: var(--space-sm);
}

.chart-container-wrap {
  position: relative; /* positioning context for .computing-placeholder below */
}

/* min-height reserves the same box Highcharts draws into once it
   actually runs (its own default when chart.height/CSS height are both
   unspecified, as here — deliberately matched, not an arbitrary guess),
   so the panel's open animation measures the correct final height from
   the start instead of a bare empty div's 0px, animating open small and
   then jumping bigger once the chart appears a moment later. */
.chart-container {
  width: 100%;
  max-width: 100%;
  min-height: 400px;
  padding: var(--space-sm);
  box-sizing: border-box;
  overflow: hidden;
}

.computing-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  opacity: 0.7;
}

</style>

